/**
 * Custom Hook: useStudentProgress
 * Manages student progress tracking, completion state, and time tracking
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  getCourseProgress,
  markContentComplete,
  updateLastAccessed,
  trackTimeSpent,
  isContentCompleted
} from '../services/lmsProgressTracking';

export const useStudentProgress = (studentPhone, courseId) => {
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completedItems, setCompletedItems] = useState(new Set());
  const timeTracker = useRef({ startTime: null, chapterId: null, contentId: null });

  // Load progress data
  useEffect(() => {
    if (!studentPhone || !courseId) return;

    loadProgress();
  }, [studentPhone, courseId]);

  const loadProgress = async () => {
    setLoading(true);
    try {
      const result = await getCourseProgress(studentPhone, courseId);
      if (result.success) {
        setProgress(result.data);
        setCompletedItems(new Set(result.data.completedItems || []));
      }
    } catch (error) {
      console.error('Failed to load progress:', error);
    } finally {
      setLoading(false);
    }
  };

  // Mark item as complete
  const markComplete = useCallback(async (chapterId, contentId, metadata = {}) => {
    const key = `${chapterId}_${contentId}`;
    
    // Optimistic update
    setCompletedItems(prev => {
      const newSet = new Set(prev);
      newSet.add(key);
      return newSet;
    });

    try {
      await markContentComplete(studentPhone, courseId, chapterId, contentId, metadata);
      await loadProgress(); // Refresh progress
    } catch (error) {
      console.error('Failed to mark complete:', error);
    }
  }, [studentPhone, courseId]);

  // Update last accessed content
  const markAsAccessed = useCallback(async (chapterId, contentId) => {
    try {
      await updateLastAccessed(studentPhone, courseId, chapterId, contentId);
      
      // Start time tracking for this content
      stopTimeTracking(); // Stop previous tracking
      timeTracker.current = {
        startTime: Date.now(),
        chapterId,
        contentId,
      };
    } catch (error) {
      console.error('Failed to update last accessed:', error);
    }
  }, [studentPhone, courseId]);

  // Stop time tracking and send data
  const stopTimeTracking = useCallback(async () => {
    if (timeTracker.current.startTime && timeTracker.current.contentId != null) {
      const timeSpent = Math.floor((Date.now() - timeTracker.current.startTime) / 1000);
      const { chapterId, contentId } = timeTracker.current;

      if (timeSpent > 5) {
        await trackTimeSpent(studentPhone, courseId, chapterId, contentId, timeSpent);
      }

      timeTracker.current = { startTime: null, chapterId: null, contentId: null };
    }
  }, [studentPhone, courseId]);

  // Check if specific content is completed
  const isCompleted = useCallback((chapterId, contentId) => {
    const key = `${chapterId}_${contentId}`;
    return completedItems.has(key) || isContentCompleted(studentPhone, courseId, chapterId, contentId);
  }, [completedItems, studentPhone, courseId]);

  // Stop tracking on unmount
  useEffect(() => {
    return () => {
      stopTimeTracking();
    };
  }, [stopTimeTracking]);

  return {
    progress,
    loading,
    completedItems: Array.from(completedItems),
    markComplete,
    markAsAccessed,
    isCompleted,
    stopTimeTracking,
    refreshProgress: loadProgress
  };
};
